/**
 * OfflineBanner — Sticky top bar shown when the device is offline or syncing.
 *
 * Usage (drop into App.tsx or any layout component):
 *   import OfflineBanner from "../components/OfflineBanner";
 *   <OfflineBanner />
 *
 * It reads from the useOffline hook, so no props needed.
 */

import React, { useEffect, useRef } from "react";
import { useOffline } from "../hooks/useOffline";
import { colors, fw, fontFamily } from "../theme";

const OfflineBanner: React.FC = () => {
  const { isOnline, pendingCount, isSyncing, syncNow } = useOffline();
  const prevOnline = useRef(isOnline);

  // Announce transition back online (screen-reader & visual cue)
  useEffect(() => {
    if (!prevOnline.current && isOnline) {
      // just came back online — banner will hide after the component re-renders
    }
    prevOnline.current = isOnline;
  }, [isOnline]);

  // Nothing to show if online and queue is empty
  if (isOnline && pendingCount === 0 && !isSyncing) return null;

  const isSyncPending = pendingCount > 0 || isSyncing;

  return (
    <>
      {/* Keyframes injected once */}
      <style>{`
        @keyframes ams-banner-slide {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        @keyframes ams-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .ams-retry-btn:hover { filter: brightness(1.15); }
      `}</style>

      <div
        role="alert"
        aria-live="assertive"
        style={{
          position:    "fixed",
          top:         0,
          left:        0,
          right:       0,
          zIndex:      9999,
          background:  isOnline ? "#16a34a" : colors.primary,
          color:       "#ffffff",
          padding:     "10px 20px",
          display:     "flex",
          alignItems:  "center",
          justifyContent: "center",
          gap:         "14px",
          flexWrap:    "wrap",
          fontSize:    "13px",
          fontWeight:  fw.semibold,
          fontFamily,
          animation:   "ams-banner-slide 0.3s ease both",
          boxShadow:   "0 4px 16px rgba(0,0,0,0.25)",
        }}
      >
        {/* Icon */}
        {isSyncing ? (
          <span
            aria-label="Syncing"
            style={{
              display: "inline-block",
              width: "16px", height: "16px",
              border: "2px solid rgba(255,255,255,0.4)",
              borderTopColor: "#ffffff",
              borderRadius: "50%",
              animation: "ams-spin 0.7s linear infinite",
            }}
          />
        ) : isOnline ? (
          <span aria-hidden>✓</span>
        ) : (
          <span aria-hidden>⚠</span>
        )}

        {/* Message */}
        <span>
          {isSyncing
            ? `Syncing ${pendingCount} saved action${pendingCount !== 1 ? "s" : ""}…`
            : isOnline && pendingCount > 0
            ? `${pendingCount} action${pendingCount !== 1 ? "s" : ""} saved offline — ready to sync`
            : "You're offline — changes will save locally and sync when connected"}
        </span>

        {/* Retry button — shown when online but still has pending items */}
        {isOnline && pendingCount > 0 && !isSyncing && (
          <button
            className="ams-retry-btn"
            onClick={syncNow}
            style={{
              background:   "rgba(255,255,255,0.2)",
              border:       "1px solid rgba(255,255,255,0.4)",
              borderRadius: "6px",
              color:        "#ffffff",
              fontSize:     "12px",
              fontWeight:   fw.bold,
              padding:      "4px 12px",
              cursor:       "pointer",
              fontFamily,
              transition:   "filter 0.15s ease",
            }}
          >
            Sync Now
          </button>
        )}
      </div>

      {/* Spacer so page content isn't hidden under the banner */}
      <div style={{ height: "40px" }} />
    </>
  );
};

export default OfflineBanner;
